import {VersionName} from '@github-ui/copilot-chat/components/VersionName'
import {NestedListView} from '@github-ui/nested-list-view'
import {NestedListItem} from '@github-ui/nested-list-view/NestedListItem'
import {NestedListItemActionBar} from '@github-ui/nested-list-view/NestedListItemActionBar'
import {NestedListItemLeadingContent} from '@github-ui/nested-list-view/NestedListItemLeadingContent'
import {NestedListItemLeadingVisual} from '@github-ui/nested-list-view/NestedListItemLeadingVisual'
import {NestedListItemTitle} from '@github-ui/nested-list-view/NestedListItemTitle'
import {NestedListViewHeader} from '@github-ui/nested-list-view/NestedListViewHeader'
import {NestedListViewHeaderTitle} from '@github-ui/nested-list-view/NestedListViewHeaderTitle'
import {ActionList, CounterLabel} from '@primer/react'
import {clsx} from 'clsx'
import type {ReactNode} from 'react'

import {escapeHtml} from '../../../../utils/escape-html'
import {type DraftOrExistingIssue, getRevisionNumber} from '../../content-preview-types'
import {useContentPreview} from '../../ContentPreviewContext'
import {IssueIcon} from '../IssueIcon'
import styles from './RelationshipList.module.css'

export interface RelationshipListAction {
  key: string
  label: string
  icon: ReactNode
  onSelect: (item: DraftOrExistingIssue) => void
}

export interface RelationshipListItem {
  item: DraftOrExistingIssue
  children?: RelationshipListItem[]
}

export interface RelationshipListProps {
  title: string
  items: RelationshipListItem[]
  totalCount: number
  actions: RelationshipListAction[]
}

function RelationshipListItemComponent({
  listItem,
  actions,
}: {
  listItem: RelationshipListItem
  actions: RelationshipListAction[]
}) {
  const {openItem, versionedItems, items} = useContentPreview()
  const {item} = listItem
  const version = getRevisionNumber(item.id, versionedItems, items)

  return (
    <NestedListItem
      key={item.tag}
      title={
        <NestedListItemTitle
          value={escapeHtml(item.name)}
          onClick={() => openItem(item.id, true)}
          trailingBadges={
            version != null ? [<VersionName key={`${item.tag}#${version}`} version={version} />] : undefined
          }
        />
      }
      secondaryActions={
        <NestedListItemActionBar
          staticMenuActions={actions.map(action => ({
            key: action.key,
            render: () => (
              <ActionList.Item key={`${item.tag}-${action.key}`} onSelect={() => action.onSelect(item)}>
                <ActionList.LeadingVisual>{action.icon}</ActionList.LeadingVisual>
                {action.label}
              </ActionList.Item>
            ),
          }))}
        />
      }
      subItems={
        listItem.children?.length
          ? listItem.children?.map(childItem => (
              <RelationshipListItemComponent key={childItem.item.tag} listItem={childItem} actions={actions} />
            ))
          : undefined
      }
      defaultExpanded
    >
      <NestedListItemLeadingContent>
        <NestedListItemLeadingVisual>
          <IssueIcon {...item} />
        </NestedListItemLeadingVisual>
      </NestedListItemLeadingContent>
    </NestedListItem>
  )
}

export function RelationshipList({title, items, totalCount, actions}: RelationshipListProps) {
  if (items.length === 0) return null

  return (
    <div className={clsx(styles.container, 'mt-2 pb-1 border rounded-2')}>
      <NestedListView
        title={title}
        header={
          <NestedListViewHeader
            title={
              <NestedListViewHeaderTitle title={title} className="p-0">
                <CounterLabel>{totalCount}</CounterLabel>
              </NestedListViewHeaderTitle>
            }
            className="border-0 bgColor-default tmp-px-3"
          />
        }
      >
        {items.map(listItem => (
          <RelationshipListItemComponent key={listItem.item.tag} listItem={listItem} actions={actions} />
        ))}
      </NestedListView>
    </div>
  )
}
